import type {AppError, Result} from '@packages/utils';
import {type UseMutationResult, useMutation} from '@tanstack/react-query';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import type {MutationConfig} from '../../lib/react-query';
import {
  type LoginResponse,
  LoginResponseSchema,
} from '../entities/login/schema';

export type ResultLogin = Result<LoginResponse, AppError>;

export type ILogin = {
  email: string;
  password: string;
  skillCheckHistoryId?: string;
  entrysheetBuilderId?: string;
  version?: string;
};

type IUseLoginOptions = {
  config?: MutationConfig<typeof login>;
  onMutateFnc?: () => void;
  onSuccessFnc: (data: LoginResponse) => void;
  onErrorFnc: (error: unknown) => void;
  onSettledFnc?: () => void;
} & ILogin;

export const login = async ({
  email,
  password,
  skillCheckHistoryId,
  entrysheetBuilderId,
  version = DEFAULT_API_VERSION,
}: ILogin): Promise<ResultLogin> => {
  const param: Record<string, string> = {
    email,
    password,
  };
  if (skillCheckHistoryId) {
    param.skillCheckHistoryId = skillCheckHistoryId;
  }

  if (entrysheetBuilderId) {
    param.entrysheetBuilderId = entrysheetBuilderId;
  }

  const result: Result<LoginResponse, AppError> = await defaultClient.post(
    `/${version}/users/signin`,
    LoginResponseSchema,
    {
      ...param,
    },
  );

  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useLogin = ({
  email,
  password,
  skillCheckHistoryId,
  entrysheetBuilderId,
  version = DEFAULT_API_VERSION,
  onMutateFnc,
  onSuccessFnc,
  onErrorFnc,
  onSettledFnc,
}: IUseLoginOptions): UseMutationResult<ResultLogin, AppError> => {
  return useMutation({
    async mutationFn() {
      return login({
        email,
        password,
        skillCheckHistoryId,
        entrysheetBuilderId,
        version,
      });
    },
    onMutate() {
      onMutateFnc?.();
    },
    onSuccess(data) {
      if (!data.ok) return;
      onSuccessFnc(data.value);
    },
    onError(error) {
      onErrorFnc(error);
    },
    onSettled() {
      onSettledFnc?.();
    },
  });
};
